import { useEffect, useState } from 'react';
import {
  Avatar,
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Link,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useSelector } from 'react-redux';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import {
  Language,
  License,
  Specialty,
  useGetProviderQuery,
} from '../../../generated/graphql';
import Schedule from './scheduling/schedule';
import { Specialties } from './specialties';
import { Icon } from '../../icon/icon';
import { formatProviderNameWithCredential } from '../../../helpers/format-helpers';
import { theme } from '../../../styles/theme';
import { trackAdditionalMatchSelectedOrBooked } from '../../../helpers/analytics';
import { selectLdFlags } from '../../../redux/ld-flags';

export type DetailModalProps = {
  id?: string;
  firstName?: string;
  lastName?: string;
  photo?: string;
  biography?: string;
  primaryCredential?: string;
  licenses?: License[];
  languages?: Language[];
  specialties: Specialty[];
  selectProvider: (appointmentTime?: string) => Promise<{ status: string }>;
  wasProviderAdditionalMatch?: boolean;
  patientAddressState?: string;
};

const DetailModal = ({
  id,
  firstName,
  lastName,
  photo,
  biography,
  primaryCredential,
  licenses,
  languages,
  specialties,
  selectProvider,
  wasProviderAdditionalMatch,
  patientAddressState,
}: DetailModalProps) => {
  const [active, setActive] = useState(false);
  const [confirmingAppointment, setConfirmingAppointment] = useState(false);
  const toggleActive = () => !confirmingAppointment && setActive(!active);

  // TODO: This query is useless because the top-level getProviderMatches query returns
  // all required info anyway.
  const { data, loading } = useGetProviderQuery({
    variables: { id },
  });

  const providerDetail = {
    ...data?.getProvider,
    licenses,
    languages,
  };

  useEffect(() => {
    if (active && wasProviderAdditionalMatch) {
      trackAdditionalMatchSelectedOrBooked(providerDetail.id, 'selected');
    }
  }, [active]);

  const useFullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const { shouldUseIntakeV2Point1 } = useSelector(selectLdFlags);

  return (
    <>
      {shouldUseIntakeV2Point1 ? (
        <Grid container alignItems="flex-end" sx={{ pl: 10, pr: 4 }}>
          <Grid container alignItems="center" item xs={6} sm={6}>
            <LocationOnIcon sx={{ color: 'text.secondary', pl: 0 }} />
            <Typography variant="caption" color="text.secondary">
              Virtual in {patientAddressState}
            </Typography>
          </Grid>
          <Grid item xs={6} sm={6} container justifyContent="flex-end">
            <Link
              style={{ cursor: 'pointer' }}
              onClick={toggleActive}
              variant="body2"
              underline="none"
              sx={{ pl: 1 }}
            >
              View bio
            </Link>
          </Grid>
        </Grid>
      ) : (
        <Link
          style={{ cursor: 'pointer' }}
          onClick={toggleActive}
          variant="body2"
          underline="none"
          sx={{ pl: 11 }}
        >
          View bio
        </Link>
      )}
      <Dialog
        open={active}
        onClose={toggleActive}
        maxWidth="md"
        fullScreen={useFullScreen}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          {formatProviderNameWithCredential(
            firstName,
            lastName,
            primaryCredential
          )}
          <IconButton onClick={toggleActive} sx={{ mr: -1 }}>
            <Icon icon="Xmark" />
          </IconButton>
        </DialogTitle>
        {!loading && (
          <DialogContent>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Avatar
                  src={photo}
                  alt={`${formatProviderNameWithCredential(
                    firstName,
                    lastName,
                    primaryCredential
                  )}}'s photo`}
                  sx={{
                    width: '100%',
                    height: 'auto',
                    maxWidth: 320,
                    maxHeight: 320,
                    mb: 3,
                  }}
                  variant="rounded"
                />
                <Specialties specialties={specialties} />
                <Typography variant="h6" gutterBottom>
                  Biography
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {biography}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box
                  sx={{
                    position: { xs: 'default', sm: 'sticky' },
                    top: { xs: 'auto', sm: 0 },
                  }}
                >
                  <Schedule
                    provider={providerDetail}
                    selectProvider={async appointmentTime =>
                      selectProvider(appointmentTime)
                    }
                    wasProviderAdditionalMatch={wasProviderAdditionalMatch}
                    confirmingAppointment={confirmingAppointment}
                    setConfirmingAppointment={setConfirmingAppointment}
                  />
                </Box>
              </Grid>
            </Grid>
          </DialogContent>
        )}
      </Dialog>
    </>
  );
};

export default DetailModal;
